import React, { useCallback, useContext, useEffect, useState } from 'react';
import { View, Pressable } from 'react-native';
import { useMutation } from '@apollo/client';
import { useForm, FormProvider } from 'react-hook-form';

import { AuthContext } from 'app/src/contexts/AuthContext';
import { Button, ButtonGroup } from 'app/src/elements/buttons';
import { Fields, defaultValues } from 'app/src/elements/hookForms';
import NestedFields from 'app/src/elements/NestedFields';
import MutationErrors from 'app/src/elements/MutationErrors';
import { Text } from 'app/src/styles';

import { FIELDS, LINE_FIELDS, EMPTY_LINE } from './new';
import { UPDATE_TXN } from '../Txns/queries';

import {
  specialAccount,
  prepareTxnInput,
  creditLessDebitTxnLine,
} from '../Txns/utils';

const EditInvoice = ({ txn, setShowModal, refetch }) => {
  const { accounts, paymentTerms } = useContext(AuthContext);

  const [nestedResources, setNestedResources] = useState(
    txn.txnLines
      .filter((txnLine, index) => index > 0)
      .map(creditLessDebitTxnLine)
  );
  const [formStatus, setFormStatus] = useState();
  const [mutationErrors, setMutationErrors] = useState();

  const {
    register, control, handleSubmit, setValue, watch, formState: { errors }
  } = useForm({
    defaultValues: defaultValues({
      fields: FIELDS,
      resource: { ...txn, balanceAccountId: txn.txnLines[0].account.id },
      nestedFields: [{
        nestedResources,
        prefix: 'txnLinesAttributes',
        fields: LINE_FIELDS,
      }],
    })
  });

  const [updateTxn] = useMutation(UPDATE_TXN, {
    onCompleted: data => {
      if (data.updateTxn.errors?.length) {
        setFormStatus(null);
        setMutationErrors(data.updateTxn.errors);
      } else {
        refetch();
        setShowModal(false);
      }
    }
  });

  const onSubmit = useCallback(input => {
    setFormStatus('submitting');
    input.type = 'Invoice';
    input.balanceAccountId =
      specialAccount({ accounts, special: 'ar' }).value;

    updateTxn({ variables: prepareTxnInput(input, FIELDS) });
  });

  const watchAll = watch();

  const addLine = () => {
    const txnLinesWatch = watchAll?.txnLinesAttributes;
    const lastLine = txnLinesWatch ? txnLinesWatch[txnLinesWatch.length - 1] :
      txn.txnLines[txn.txnLines.length - 1];
    const prefix = `txnLinesAttributes.${nestedResources.length}`;

    register(`${prefix}.accountId`, { value: '' });
    register(`${prefix}.creditLessDebit`, { value: '' });
    register(`${prefix}.memo`, { value: '' });
    register(`${prefix}.assetId`, {
      value: lastLine?.assetId || lastLine?.asset?.id
    });
    register(`${prefix}.saleAssetId`, {
      value: lastLine?.saleAssetId || lastLine?.saleAsset?.id
    });
    setNestedResources([...nestedResources, EMPTY_LINE]);
  }

  useEffect(() => {
    const txnLinesWatch = watchAll?.txnLinesAttributes;

    if (txnLinesWatch) {
      const lastLine = txnLinesWatch[txnLinesWatch.length - 1];

      if (lastLine.creditLessDebit) { addLine(); }
    }
  }, [watchAll]);

  const fields = { ...FIELDS };

  fields.paymentTermId = {
    label: 'Payment Terms',
    select: (paymentTerms || []),
    rules: { required: false },
    blank: 'null',
  };

  const lineFields = { ...LINE_FIELDS };

  lineFields.assetId = {
    ...lineFields.assetId,
    txn: txn,
    onFeeCreated: data => {
      refetch();
      setShowModal(false);
    }
  }

  return (
    <View>
      <FormProvider watch={watch}>
        <Fields
          fields={fields}
          control={control}
          errors={errors}
          setValue={setValue}
        />

        <NestedFields
          prefix="txnLinesAttributes"
          resources={nestedResources}
          fields={lineFields}
          register={register}
          control={control}
          errors={errors}
          setValue={setValue}
        />

        <Pressable onPress={addLine}>
          <Text style={{ fontSize: 14 }}>+ Add Line</Text>
        </Pressable>

        <MutationErrors errors={mutationErrors} />

        <ButtonGroup>
          <Button label="Update Invoice" onPress={handleSubmit(onSubmit)} />
        </ButtonGroup>
      </FormProvider>
    </View>
  );
};

export default EditInvoice;
